// @givmo/board — Form 990 › Compensation decisions: the board's decisions on pay, read from its own
// records (CBG-YR4-PAY, frame c2; the backend's `board-compensation-decisions.md` §8.3, §8.4). The
// Form 990 page's second part since CBG-UX8-MENU (D-CBG-35 (1)); its own component, unchanged by the
// move.
//
// ══ WHAT THE LIST SHOWS ═════════════════════════════════════════════════════════════════════════
//
// Grouped by person, newest decision first — the backend's grouping and order, kept. Each person's
// newest decision is shown whole, in the order of the items 26 CFR 53.4958-6(c)(3)(i) lists for an
// authorized body's records: the decision and its date; who took part (who was present, who was
// recused and the basis recorded, the vote, the board's own recorded determination); what the board
// relied on and how it obtained it; and when it was written down, beside the date the regulation's
// own rule gives. Each earlier decision is one line — where it came from — that opens its card in
// place. An absent part is simply absent: nothing marks a decision as having or lacking what the
// regulation lists (the contract's §9).
//
// THE SENTENCES THAT CONCLUDE A PART ARE THE BACKEND'S, printed as served: the timing line and the
// regulation's line, a decision from the minute book's `provenance_statement` (review-176's P3: as
// served, no short form of the web's own), and the board's own recorded determination. The web
// composes only the plain lists around them.
//
// THE LIST IS A PART VI ANSWER SURFACE (the derivation §4.6): evidence beside the organization's own
// answer, never a verdict; the plan's gate puts CBG-ST2's consult-counsel line here before any
// third party uses it.
//
// Acts: "Add a decision from your records" and an index entry's change and removal are
// `board_record:write`, as is the organization's Form 990 answer on any decision. A motion's or a
// consent's details are changed where they were entered: the card's title opens that page.

import { useState, type ReactNode } from 'react';
import { useMutation } from '@tanstack/react-query';
import {
  isGivmoError,
  type BoardCompensationDecisionOut,
  type BoardCompensationDocumentOut,
  type BoardCompensationPersonOut,
} from '@givmo/api-client';
import { Button, Card, CardBody, RowActions, Skeleton, toast } from '@givmo/design-system';
import { useBoardHost, useBoardPermission } from '../../board-host';
import { useBoardAnswersForm990, useBoardOperations } from '../../board-runtime';
import { Link } from '../../board-routing';
import { GroupLabel, HowThisWorks } from '../../components/fine-print';
import {
  castLines,
  cardHead,
  decisionCommittee,
  earlierLine,
  form990LineOf,
  presentLine,
  voteLine,
} from './compensation-decode';
import { AnswerDialog, IndexEntryDialog } from './compensation-dialogs';
import { useCompensationList, useRefreshCompensation } from './compensation-queries';
import { compensationWords as w } from './compensation-words';

const LABEL = 'font-sans text-s12 font-w600 tracking-givmo text-text-secondary';
const TEXT = 'font-sans text-s14 font-w400 leading-h15 tracking-givmo text-text-strong';
const QUIET = 'font-sans text-s13 font-w400 leading-h15 tracking-givmo text-text-tertiary';
const SECONDARY = 'font-sans text-s14 font-w400 leading-h15 tracking-givmo text-text-secondary';
const DOC_LINK =
  'text-left font-sans text-s14 font-w600 tracking-givmo text-brand-600 outline-none transition-colors [overflow-wrap:anywhere] hover:text-brand-700 focus-visible:underline';
const ACT_LINK =
  'font-sans text-s14 font-w600 tracking-givmo text-brand-600 outline-none transition-colors hover:text-brand-700 focus-visible:underline';
const TITLE_LINK =
  'font-sans text-s15 font-w600 tracking-givmo text-text-strong underline-offset-2 outline-none hover:underline focus-visible:underline';

/** One part of a decision, in the regulation's order: its label beside (or above) what it says. */
function Part({ label, children, testId }: { label: string; children: ReactNode; testId: string }) {
  return (
    <div
      className="flex flex-col gap-4 border-t border-border-subtle pt-12 sm:flex-row sm:gap-24"
      data-testid={testId}
    >
      <span className={`${LABEL} shrink-0 sm:w-[180px] sm:pt-2`}>{label}</span>
      <div className="flex min-w-0 flex-1 flex-col gap-6">{children}</div>
    </div>
  );
}

/**
 * A labelled fact inside a part: "Present: …", "The vote: …". `keepLines` keeps the line breaks of
 * the board's own words (an answer of several lines, as the minutes print it).
 */
function Fact({
  label,
  children,
  keepLines = false,
}: {
  label: string;
  children: ReactNode;
  keepLines?: boolean;
}) {
  return (
    <p className={keepLines ? `${TEXT} whitespace-pre-wrap` : TEXT}>
      <span className="text-text-secondary">{label}: </span>
      {children}
    </p>
  );
}

/** Where a card's title leads: the page the decision's own details live on. */
function TitleLink({ decision, title }: { decision: BoardCompensationDecisionOut; title: string }) {
  const facts = decision.decision;
  if (decision.anchor_kind === 'motion' && facts.meeting_id) {
    return (
      <Link
        to="/meetings/$meetingId/record"
        params={{ meetingId: facts.meeting_id }}
        className={TITLE_LINK}
        data-testid={`compensation-decision-title-${decision.id}`}
      >
        {title}
      </Link>
    );
  }
  if (decision.anchor_kind === 'written_consent' && facts.written_consent_id) {
    return (
      <Link
        to="/consents/$consentId"
        params={{ consentId: facts.written_consent_id }}
        className={TITLE_LINK}
        data-testid={`compensation-decision-title-${decision.id}`}
      >
        {title}
      </Link>
    );
  }
  return (
    <span
      className="font-sans text-s15 font-w600 tracking-givmo text-text-strong"
      data-testid={`compensation-decision-title-${decision.id}`}
    >
      {title}
    </span>
  );
}

function DecisionCard({
  decision,
  actions,
  onOpenDocument,
  opening,
}: {
  decision: BoardCompensationDecisionOut;
  actions: { label: string; testId: string; destructive?: boolean; onSelect: () => void }[];
  onOpenDocument: (document: BoardCompensationDocumentOut) => void;
  opening: string | null;
}) {
  const facts = decision.decision;
  const who = decision.who_took_part ?? null;
  const reliedOn = decision.relied_on ?? null;
  const documents = reliedOn?.documents ?? [];
  const present = who?.present ?? [];
  const written = decision.written_down;
  const head = cardHead(decision);
  // CBG-FIX87: a committee's decision says the committee where the board's says the board.
  const byCommittee = decisionCommittee(decision) !== null;
  const vote = voteLine(who);
  const casts = castLines(who);
  const signers = (who?.signers ?? []).map((row) => row.display_name);
  const isEntry = decision.anchor_kind === 'record';

  return (
    <Card testId={`compensation-decision-${decision.id}`}>
      <CardBody className="flex flex-col gap-12">
        <div className="flex items-start justify-between gap-12">
          <div className="flex min-w-0 flex-1 flex-wrap items-baseline justify-between gap-x-16 gap-y-2">
            <TitleLink decision={decision} title={head.title} />
            {head.when !== null ? <span className={QUIET}>{head.when}</span> : null}
          </div>
          {actions.length > 0 ? (
            <RowActions
              label={w.rowActions(decision.person_name)}
              testId={`compensation-decision-actions-${decision.id}`}
              actions={actions}
            />
          ) : null}
        </div>

        {facts.motion_text ? (
          <Part label={w.theDecision} testId={`compensation-decision-${decision.id}-decision`}>
            <blockquote
              className={`${TEXT} whitespace-pre-wrap border-l-2 border-border-strong pl-12`}
            >
              {facts.motion_text}
            </blockquote>
          </Part>
        ) : facts.record_id ? (
          // A consent's, or one from the minute book: the record itself holds the terms, the room
          // and what the board relied on, and it opens in the minute book.
          <Part label={w.theDecision} testId={`compensation-decision-${decision.id}-decision`}>
            {isEntry && facts.record_locator ? (
              <Fact label={w.whereInRecord}>{facts.record_locator}</Fact>
            ) : null}
            <Link
              to="/records/$recordId"
              params={{ recordId: facts.record_id }}
              className={`${ACT_LINK} self-start`}
              data-testid={`compensation-decision-record-${decision.id}`}
            >
              {w.openInBook}
            </Link>
          </Part>
        ) : null}

        {isEntry ? null : (
          <Part label={w.whoTookPart} testId={`compensation-decision-${decision.id}-who`}>
            {present.length > 0 ? <Fact label={w.present}>{presentLine(present)}</Fact> : null}
            {(who?.recused ?? []).map((row) => (
              <div key={row.display_name} className="flex flex-col gap-2">
                <Fact label={w.recused}>{row.display_name}</Fact>
                {row.basis_note ? (
                  <p className={`${QUIET} whitespace-pre-wrap`}>{row.basis_note}</p>
                ) : null}
              </div>
            ))}
            {vote !== null ? <Fact label={w.theVote}>{vote}</Fact> : null}
            {casts.map((line) => (
              <p key={line} className={QUIET}>
                {line}
              </p>
            ))}
            {who?.determination_statement ? (
              <p className={QUIET}>{who.determination_statement}</p>
            ) : null}
            {who?.determination_basis ? (
              <p className={`${QUIET} whitespace-pre-wrap`}>
                {w.recordedBasis(who.determination_basis)}
              </p>
            ) : null}
            {signers.length > 0 ? <Fact label={w.signedBy}>{signers.join(', ')}</Fact> : null}
          </Part>
        )}

        {isEntry ? null : reliedOn !== null &&
          (documents.length > 0 || reliedOn.how_obtained || reliedOn.reason_outside_range) ? (
          <Part
            label={byCommittee ? w.committeeReliedOn : w.reliedOn}
            testId={`compensation-decision-${decision.id}-relied`}
          >
            {documents.length > 0 ? (
              <ul className="flex flex-col gap-4">
                {documents.map((document) => (
                  <li key={document.id}>
                    <button
                      type="button"
                      className={DOC_LINK}
                      disabled={opening === document.id}
                      onClick={() => onOpenDocument(document)}
                      data-testid={`compensation-document-${document.id}`}
                    >
                      {document.file_name}
                    </button>
                  </li>
                ))}
              </ul>
            ) : null}
            {reliedOn.how_obtained ? (
              <Fact label={w.obtained} keepLines>
                {reliedOn.how_obtained}
              </Fact>
            ) : null}
            {reliedOn.reason_outside_range ? (
              <Fact label={byCommittee ? w.committeeReasonOutside : w.reasonOutside} keepLines>
                {reliedOn.reason_outside_range}
              </Fact>
            ) : null}
          </Part>
        ) : null}

        {written.statement || written.regulation_statement ? (
          <Part label={w.writtenDown} testId={`compensation-decision-${decision.id}-written`}>
            {written.statement ? <p className={TEXT}>{written.statement}</p> : null}
            {written.regulation_statement ? (
              <p className={QUIET}>{written.regulation_statement}</p>
            ) : null}
          </Part>
        ) : null}
      </CardBody>
    </Card>
  );
}

function PersonGroup({
  person,
  answersForm990,
  actionsFor,
  onOpenDocument,
  opening,
}: {
  person: BoardCompensationPersonOut;
  answersForm990: boolean;
  actionsFor: (
    decision: BoardCompensationDecisionOut,
  ) => { label: string; testId: string; destructive?: boolean; onSelect: () => void }[];
  onOpenDocument: (
    decision: BoardCompensationDecisionOut,
    document: BoardCompensationDocumentOut,
  ) => void;
  opening: string | null;
}) {
  const [shown, setShown] = useState<ReadonlySet<string>>(new Set());
  const [newest, ...earlier] = person.decisions ?? [];
  const line = answersForm990 ? form990LineOf(person.form_990_line) : null;
  const facts = [person.position || null, line !== null ? w.yourAnswer(line) : null].filter(
    (part): part is string => part !== null,
  );
  if (newest === undefined) return null;

  const card = (decision: BoardCompensationDecisionOut) => (
    <DecisionCard
      decision={decision}
      actions={actionsFor(decision)}
      onOpenDocument={(document) => onOpenDocument(decision, document)}
      opening={opening}
    />
  );

  return (
    <section
      className="flex flex-col gap-10"
      data-testid={`compensation-person-${person.group_key}`}
    >
      <div className="flex flex-wrap items-baseline justify-between gap-x-16 gap-y-2">
        <div className="flex min-w-0 flex-col gap-2 sm:flex-row sm:flex-wrap sm:items-baseline sm:gap-x-10">
          <h2 className="font-sans text-s16 font-w700 tracking-givmo text-text-strong">
            {person.person_name}
          </h2>
          {facts.length > 0 ? <span className={SECONDARY}>{facts.join(' · ')}</span> : null}
        </div>
        {person.last_decided_year !== null && person.last_decided_year !== undefined ? (
          <span className={QUIET}>{w.lastDecided(person.last_decided_year)}</span>
        ) : null}
      </div>
      {card(newest)}
      {earlier.length > 0 ? (
        <div
          className="flex flex-col gap-6"
          data-testid={`compensation-earlier-${person.group_key}`}
        >
          <GroupLabel>{w.earlier}</GroupLabel>
          {earlier.map((decision) => {
            const open = shown.has(decision.id);
            return (
              <div key={decision.id} className="flex flex-col gap-8">
                <button
                  type="button"
                  aria-expanded={open}
                  className="self-start text-left font-sans text-s14 font-w400 leading-h15 tracking-givmo text-text-secondary outline-none transition-colors hover:text-text-strong focus-visible:underline"
                  onClick={() =>
                    setShown((current) => {
                      const next = new Set(current);
                      if (next.has(decision.id)) next.delete(decision.id);
                      else next.add(decision.id);
                      return next;
                    })
                  }
                  data-testid={`compensation-earlier-line-${decision.id}`}
                >
                  {earlierLine(decision)}
                </button>
                {open ? card(decision) : null}
              </div>
            );
          })}
        </div>
      ) : null}
    </section>
  );
}

export function CompensationPart() {
  const { orgRef, isReady } = useBoardHost();
  const operations = useBoardOperations();
  const answersForm990 = useBoardAnswersForm990();
  const canWrite = useBoardPermission('board_record:write');
  const listQuery = useCompensationList(orgRef, isReady);
  const refresh = useRefreshCompensation(orgRef);
  const [adding, setAdding] = useState(false);
  const [editing, setEditing] = useState<BoardCompensationDecisionOut | null>(null);
  const [answering, setAnswering] = useState<BoardCompensationDecisionOut | null>(null);
  const org = orgRef ?? '';

  const removeEntry = useMutation({
    mutationFn: async (entry: BoardCompensationDecisionOut) => {
      await operations.officerRemoveBoardCompensationIndexEntry({
        path: { org_ref: org, decision_id: entry.id },
        throwOnError: true,
      });
    },
    onSuccess: () => {
      toast.success(w.removed);
      void refresh();
    },
    onError: (caught) => toast.error(isGivmoError(caught) ? caught.message : w.removeFailed),
  });

  const open = useMutation({
    mutationFn: async (input: { decisionId: string; documentId: string }) => {
      const { data } = await operations.officerGetBoardCompensationDecisionDocumentDownload({
        path: { org_ref: org, decision_id: input.decisionId, document_id: input.documentId },
        throwOnError: true,
      });
      return data;
    },
    // A short-lived link to the exact version the fingerprint names, minted for this request and
    // never stored: opened, not kept.
    onSuccess: (link) => void window.open(link.download_url, '_blank', 'noopener,noreferrer'),
    onError: (caught) => toast.error(isGivmoError(caught) ? caught.message : w.openFailed),
  });

  const actionsFor = (decision: BoardCompensationDecisionOut) => {
    if (!canWrite) return [];
    if (decision.anchor_kind === 'record') {
      return [
        {
          label: w.edit,
          testId: `compensation-entry-edit-${decision.id}`,
          onSelect: () => setEditing(decision),
        },
        {
          label: w.remove,
          testId: `compensation-entry-remove-${decision.id}`,
          destructive: true,
          onSelect: () => removeEntry.mutate(decision),
        },
      ];
    }
    return answersForm990 && decision.labels_editable
      ? [
          {
            label: w.changeAnswer,
            testId: `compensation-answer-${decision.id}`,
            onSelect: () => setAnswering(decision),
          },
        ]
      : [];
  };

  const people = listQuery.data;
  const lead = (
    <div className="flex flex-col gap-4">
      <p className={SECONDARY}>
        {w.listLine} <HowThisWorks testId="compensation-how" lines={w.howLines} />
      </p>
    </div>
  );
  const addButton = canWrite ? (
    <button
      type="button"
      className={`${ACT_LINK} self-start`}
      onClick={() => setAdding(true)}
      data-testid="compensation-add-from-records"
    >
      {w.addFromRecords}
    </button>
  ) : null;

  let body: ReactNode;
  if (!isReady || orgRef === null || listQuery.isPending) {
    body = (
      <div className="flex flex-col gap-12" data-testid="compensation-loading">
        <Skeleton className="h-84 w-full rounded-lg" />
        <Skeleton className="h-48 w-full rounded-lg" />
      </div>
    );
  } else if (listQuery.isError || people === undefined) {
    body = (
      <Card testId="compensation-error">
        <CardBody className="flex flex-col items-start gap-12">
          <p className={SECONDARY}>{w.loadFailed}</p>
          <Button
            appearance="saas"
            tone="secondary"
            onClick={() => void listQuery.refetch()}
            testId="compensation-error-retry"
          >
            {w.tryAgain}
          </Button>
        </CardBody>
      </Card>
    );
  } else if (people === null) {
    body = (
      <p className={SECONDARY} data-testid="compensation-not-served">
        {w.notServed}
      </p>
    );
  } else {
    body = (
      <>
        {lead}
        {people.length === 0 ? (
          <p className={SECONDARY} data-testid="compensation-empty">
            {w.empty}
          </p>
        ) : (
          people.map((person) => (
            <PersonGroup
              key={person.group_key}
              person={person}
              answersForm990={answersForm990}
              actionsFor={actionsFor}
              onOpenDocument={(decision, document) =>
                open.mutate({ decisionId: decision.id, documentId: document.id })
              }
              opening={open.isPending ? (open.variables?.documentId ?? null) : null}
            />
          ))
        )}
        {addButton}
      </>
    );
  }

  return (
    <div className="flex flex-col gap-24 pt-8" data-testid="compensation-decisions">
      {body}
      {adding && orgRef !== null ? (
        <IndexEntryDialog
          orgRef={orgRef}
          entry={null}
          answersForm990={answersForm990}
          onClose={() => setAdding(false)}
          onSaved={() => {
            setAdding(false);
            void refresh();
          }}
        />
      ) : null}
      {editing !== null && orgRef !== null ? (
        <IndexEntryDialog
          orgRef={orgRef}
          entry={editing}
          answersForm990={answersForm990}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            void refresh();
          }}
        />
      ) : null}
      {answering !== null && orgRef !== null ? (
        <AnswerDialog
          orgRef={orgRef}
          decision={answering}
          onClose={() => setAnswering(null)}
          onSaved={() => {
            setAnswering(null);
            void refresh();
          }}
        />
      ) : null}
    </div>
  );
}
